import React, { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { appConfig } from '@/config/app'
import { locale } from '@/locales'

type TabKey = 'subscriptions' | 'balance' | 'referrals' | 'profile'

type TabItem = {
  key: TabKey
  label: string
  path: string
  Icon: (props: { active: boolean }) => React.ReactElement
}

const ACTIVE_COLOR = '#007CFF'
const INACTIVE_COLOR = '#F5F5F5'
const TAB_BAR_WIDTH = 351
const TAB_INNER_PADDING = 7
const INDICATOR_WIDTH = 74

const tabs: TabItem[] = [
  {
    key: 'subscriptions',
    label: locale.navigation.subscriptions,
    path: appConfig.routes.subscriptions,
    Icon: ({ active }) => (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-[26px] w-[26px]">
        <path
          d="M23.1245 2.8769C19.2775 -0.958967 13.039 -0.958967 9.21802 2.8769C6.5277 5.54251 5.72191 9.37838 6.76164 12.7591L0.653198 18.8705C0.224307 19.3126 -0.074617 20.1838 0.0163599 20.808L0.406261 23.6426C0.549224 24.5788 1.42 25.463 2.35576 25.5931L5.18904 25.9831C5.81288 26.0742 6.68366 25.7881 7.12555 25.333L8.19128 24.2667C8.45121 24.0197 8.45121 23.6036 8.19128 23.3435L5.66992 20.821C5.29302 20.4439 5.29302 19.8197 5.66992 19.4427C6.04683 19.0656 6.67067 19.0656 7.04757 19.4427L9.58193 21.9782C9.82886 22.2253 10.2448 22.2253 10.4917 21.9782L13.247 19.2346C16.6131 20.2878 20.4472 19.4687 23.1245 16.8031C26.9585 12.9672 26.9585 6.71277 23.1245 2.8769ZM16.2492 12.9932C14.4557 12.9932 13.0001 11.5369 13.0001 9.74246C13.0001 7.94805 14.4557 6.49172 16.2492 6.49172C18.0428 6.49172 19.4984 7.94805 19.4984 9.74246C19.4984 11.5369 18.0428 12.9932 16.2492 12.9932Z"
          fill={active ? ACTIVE_COLOR : INACTIVE_COLOR}
        />
      </svg>
    ),
  },
  {
    key: 'balance',
    label: locale.navigation.balance,
    path: appConfig.routes.balance,
    Icon: ({ active }) => (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-[26px] w-[26px]">
        <path d="M12.9201 0.265243L9.71004 7.56044H6.5C5.96721 7.56044 5.44775 7.59945 4.9416 7.70348L6.27357 4.58254L6.32684 4.46551L6.40676 4.25745C6.44672 4.16642 6.47336 4.0884 6.51332 4.02338C8.0584 0.525321 9.78996 -0.567008 12.9201 0.265243Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
        <path d="M21.9641 7.91155L21.9375 7.89854C21.1383 7.67748 20.3258 7.56044 19.5 7.56044H11.1619L14.1588 0.759392L14.1988 0.668365C14.3852 0.733384 14.585 0.824412 14.7848 0.889431L17.7285 2.0988C19.3668 2.762 20.5123 3.4512 21.2182 4.28345C21.3381 4.4395 21.4447 4.58254 21.5512 4.7516C21.6711 4.93365 21.7643 5.11571 21.8176 5.31076C21.8709 5.4278 21.9109 5.53183 21.9375 5.64887C22.1373 6.31207 22.1506 7.06629 21.9641 7.91155Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
        <path d="M13.6926 20.3563H14.0256C14.4252 20.3563 14.7582 20.0052 14.7582 19.5761C14.7582 19.0299 14.5984 18.9519 14.252 18.8218L13.6926 18.6268V20.3563Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
        <path d="M21.3781 9.77111C20.7787 9.60206 20.1527 9.51103 19.5 9.51103H6.5C5.59426 9.51103 4.7418 9.68008 3.94262 10.0182C1.625 10.9935 0 13.2432 0 15.8569V18.3927C0 18.7048 0.0266393 19.0039 0.0665983 19.316C0.359631 23.4512 2.62397 25.6619 6.85963 25.935C7.16598 25.974 7.47234 26 7.80533 26H18.1947C23.123 26 25.7203 23.7113 25.9734 19.1599C25.9867 18.9129 26 18.6528 26 18.3927V15.8569C26 12.9831 24.042 10.5643 21.3781 9.77111ZM14.7049 17.5475C15.3176 17.7555 16.1434 18.1976 16.1434 19.5761C16.1434 20.7594 15.1977 21.7087 14.0256 21.7087H13.6926V21.9948C13.6926 22.3719 13.3863 22.671 13 22.671C12.6137 22.671 12.3074 22.3719 12.3074 21.9948V21.7087H12.1875C10.9088 21.7087 9.85656 20.6554 9.85656 19.355C9.85656 18.9779 10.1629 18.6788 10.5492 18.6788C10.9355 18.6788 11.2418 18.9779 11.2418 19.355C11.2418 19.9012 11.668 20.3563 12.1875 20.3563H12.3074V18.1586L11.2951 17.8075C10.6824 17.5995 9.85656 17.1573 9.85656 15.7789C9.85656 14.5956 10.8023 13.6463 11.9744 13.6463H12.3074V13.3602C12.3074 12.9831 12.6137 12.684 13 12.684C13.3863 12.684 13.6926 12.9831 13.6926 13.3602V13.6463H13.8125C15.0912 13.6463 16.1434 14.6996 16.1434 16C16.1434 16.3771 15.8371 16.6762 15.4508 16.6762C15.0645 16.6762 14.7582 16.3771 14.7582 16C14.7582 15.4538 14.332 14.9987 13.8125 14.9987H13.6926V17.1963L14.7049 17.5475Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
        <path d="M11.2418 15.7789C11.2418 16.3251 11.4016 16.4031 11.748 16.5331L12.3074 16.7282V14.9987H11.9744C11.5615 14.9987 11.2418 15.3498 11.2418 15.7789Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
      </svg>
    ),
  },
  {
    key: 'referrals',
    label: locale.navigation.referrals,
    path: appConfig.routes.referrals,
    Icon: ({ active }) => (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-[26px] w-[26px]">
        <path d="M11.5556 17.4834V26H7.22222C5.86039 26 5.17958 25.9999 4.75651 25.5841C4.33344 25.1684 4.33333 24.4994 4.33333 23.1611V17.4834H11.5556ZM21.6667 23.1611C21.6667 24.4994 21.6666 25.1684 21.2435 25.5841C20.8204 25.9999 20.1396 26 18.7778 26H14.4444V17.4834H21.6667V23.1611ZM23.1111 7.54731C24.4729 7.54731 25.1538 7.54742 25.5768 7.96317C25.9999 8.37891 26 9.04793 26 10.3862V11.8056C26 13.1439 25.9999 13.8129 25.5768 14.2287C25.1538 14.6444 24.4729 14.6445 23.1111 14.6445H14.4444V7.54731H23.1111ZM11.5556 14.6445H2.88889C1.52705 14.6445 0.846245 14.6444 0.423177 14.2287C0.000109126 13.8129 0 13.1439 0 11.8056V10.3862C0 9.04793 0.000109083 8.37891 0.423177 7.96317C0.846245 7.54742 1.52705 7.54731 2.88889 7.54731H11.5556V14.6445Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
        <path d="M23.1111 2.7673V2.5088C23.1111 0.797712 21.4052 -0.410528 19.7534 0.130567C17.6869 0.807451 15.8092 1.94783 14.269 3.46139L13 4.70844V6.12788H18.309C18.6195 6.12788 18.928 6.07869 19.2225 5.9822L21.2371 5.32228C22.3563 4.9557 23.1111 3.92653 23.1111 2.7673Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
        <path d="M2.88889 2.7673V2.5088C2.88889 0.797712 4.59476 -0.410528 6.24665 0.130567C8.31308 0.807451 10.1908 1.94783 11.731 3.46139L13 4.70844V6.12788H7.69102C7.38052 6.12788 7.07205 6.07869 6.77748 5.9822L4.76286 5.32228C3.64374 4.9557 2.88889 3.92653 2.88889 2.7673Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
      </svg>
    ),
  },
  {
    key: 'profile',
    label: locale.navigation.profile,
    path: appConfig.routes.profile,
    Icon: ({ active }) => (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-[26px] w-[26px]">
        <path d="M26 13C26 5.837 20.163 0 13 0C5.837 0 0 5.837 0 13C0 16.77 1.625 20.163 4.199 22.542C4.199 22.555 4.199 22.555 4.186 22.568C4.316 22.698 4.472 22.802 4.602 22.919C4.68 22.984 4.745 23.049 4.823 23.101C5.057 23.296 5.317 23.478 5.564 23.66C5.655 23.725 5.733 23.777 5.824 23.842C6.071 24.011 6.331 24.167 6.604 24.31C6.695 24.362 6.799 24.427 6.89 24.479C7.15 24.622 7.423 24.752 7.709 24.869C7.813 24.921 7.917 24.973 8.021 25.012C8.307 25.129 8.593 25.233 8.879 25.324C8.983 25.363 9.087 25.402 9.191 25.428C9.503 25.519 9.815 25.597 10.127 25.675C10.218 25.701 10.309 25.727 10.413 25.74C10.777 25.818 11.141 25.87 11.518 25.909C11.57 25.909 11.622 25.922 11.674 25.935C12.116 25.974 12.558 26 13 26C13.442 26 13.884 25.974 14.313 25.935C14.365 25.935 14.417 25.922 14.469 25.909C14.846 25.87 15.21 25.818 15.574 25.74C15.665 25.727 15.756 25.688 15.86 25.675C16.172 25.597 16.497 25.532 16.796 25.428C16.9 25.389 17.004 25.35 17.108 25.324C17.394 25.22 17.693 25.129 17.966 25.012C18.07 24.973 18.174 24.921 18.278 24.869C18.551 24.752 18.824 24.622 19.097 24.479C19.201 24.427 19.292 24.362 19.383 24.31C19.643 24.154 19.903 24.011 20.163 23.842C20.254 23.79 20.332 23.725 20.423 23.66C20.683 23.478 20.93 23.296 21.164 23.101C21.242 23.036 21.307 22.971 21.385 22.919C21.528 22.802 21.671 22.685 21.801 22.568C21.801 22.555 21.801 22.555 21.788 22.542C24.375 20.163 26 16.77 26 13ZM19.422 19.461C15.899 17.095 10.127 17.095 6.578 19.461C6.006 19.838 5.538 20.28 5.148 20.761C3.172 18.759 1.95 16.016 1.95 13C1.95 6.903 6.903 1.95 13 1.95C19.097 1.95 24.05 6.903 24.05 13C24.05 16.016 22.828 18.759 20.852 20.761C20.475 20.28 19.994 19.838 19.422 19.461Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
        <path d="M13 6.409C10.309 6.409 8.125 8.593 8.125 11.284C8.125 13.923 10.192 16.068 12.935 16.146C12.974 16.146 13.026 16.146 13.052 16.146C13.078 16.146 13.117 16.146 13.143 16.146C13.156 16.146 13.169 16.146 13.169 16.146C15.795 16.055 17.862 13.923 17.875 11.284C17.875 8.593 15.691 6.409 13 6.409Z" fill={active ? ACTIVE_COLOR : INACTIVE_COLOR} />
      </svg>
    ),
  },
]

export const MobileMenu: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const isTabActive = (tab: TabItem): boolean => {
    if (tab.key === 'subscriptions') {
      return (
        location.pathname === appConfig.routes.subscriptions ||
        location.pathname.startsWith(`${appConfig.routes.subscriptionDetailsPrefix}/`) ||
        location.pathname === appConfig.routes.installGuide
      )
    }

    if (tab.key === 'balance') {
      return location.pathname === appConfig.routes.balance
    }

    if (tab.key === 'referrals') {
      return location.pathname.startsWith(appConfig.routes.referrals)
    }

    return (
      location.pathname === appConfig.routes.profile ||
      location.pathname === appConfig.routes.dashboard
    )
  }

  const tabCount = tabs.length
  const tabSlotWidth = (TAB_BAR_WIDTH - TAB_INNER_PADDING * 2) / tabCount
  const indicatorBaseLeft = TAB_INNER_PADDING + (tabSlotWidth - INDICATOR_WIDTH) / 2

  const activeIndex = useMemo(() => {
    const index = tabs.findIndex((tab) => isTabActive(tab))
    return index >= 0 ? index : 0
  }, [location.pathname])

  const activeTab = tabs[activeIndex]
  const indicatorLeft = indicatorBaseLeft + activeIndex * tabSlotWidth

  const bottomInset = 'var(--app-device-safe-area-bottom, env(safe-area-inset-bottom, 0px))'

  return (
    <div className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-0.5" style={{ bottom: `max(14px, ${bottomInset})` }}>
      <nav className="pointer-events-auto relative h-[62px] w-[351px]" aria-label="Навигация Stride">
        <div
          className="absolute inset-0 rounded-[1000px] opacity-[0.67]"
          style={{
            background:
              'linear-gradient(0deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.06) 100%), linear-gradient(0deg, rgba(0,0,0,0.60) 0%, rgba(0,0,0,0.60) 100%), rgba(204,204,204,0.50)',
            boxShadow: '0 8px 40px 0 rgba(0,0,0,0.12)',
            backdropFilter: 'blur(18px)',
          }}
        />
        <div className="absolute inset-0 rounded-[296px] bg-[rgba(0,0,0,0.20)]" />

        <div
          className="absolute top-1 h-[54px] w-[74px] rounded-[100px] bg-[#121212] transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{ transform: `translateX(${indicatorLeft}px)` }}
          aria-hidden="true"
        />

        <div className="absolute inset-x-[7px] top-1 grid h-[54px]" style={{ gridTemplateColumns: `repeat(${tabCount}, minmax(0, 1fr))` }}>
          {tabs.map((tab) => {
            const isActive = tab.key === activeTab.key
            const textColor = isActive ? ACTIVE_COLOR : INACTIVE_COLOR

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => navigate(tab.path)}
                className="flex h-[54px] w-full flex-col items-center justify-start gap-[3px] px-2 pb-[7px] pt-1.5"
                aria-current={isActive ? 'page' : undefined}
                aria-label={isActive ? `${tab.label}, текущая вкладка` : tab.label}
                title={tab.label}
              >
                {tab.Icon({ active: isActive })}
                <span className="font-poppins text-[10px] font-semibold leading-3 tracking-[-0.01em]" style={{ color: textColor }}>
                  {tab.label}
                </span>
              </button>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
